import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import '../../App.css';
import './MangaPage.css';
import '../anime/AnimePage.css';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageShell from '../../components/layout/PageShell';
import useScrolled from '../../hooks/useScrolled';
import MangaApiService from '../../services/MangaApiService';
import ApiService from '../../services/ApiService';
import MangaCommentComponent from '../../components/manga/MangaCommentComponent';
import MangaAccessNotice from './MangaAccessNotice';
import { unwrapList, getMangaErrorMessage } from './mangaStudioUtils';
import {
    formatChapterLabel,
    getChapterId,
    getChapterTitle,
    saveMangaProgress,
    sortChapters,
} from './mangaChapters';
import MangaChapterNavigator from './MangaChapterNavigator';
import { getMangaPref, isMangaPrefOn } from '../../utils/mangaPrefs';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronsLeftIcon,
    ChevronsRightIcon,
    CloseIcon,
    ExpandIcon,
} from './MangaIcons';
import placeholderCover from '../../assets/card_placeholder.png';

const normalizePages = (chapter) => {
    if (!chapter) {
        return [];
    }
    if (Array.isArray(chapter)) {
        return chapter;
    }
    const raw = chapter.pages || chapter.pageList || chapter.files || chapter.images || chapter.pageMap;
    if (!raw) {
        return [];
    }
    if (Array.isArray(raw)) {
        return raw;
    }
    if (typeof raw === 'object') {
        return Object.values(raw);
    }
    return [];
};

const unwrapPagesResponse = (response) => {
    if (!response) {
        return [];
    }
    const payload = response?.data || response;
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }
    if (Array.isArray(payload?.content)) {
        return payload.content;
    }
    if (Array.isArray(payload?.items)) {
        return payload.items;
    }
    return normalizePages(payload);
};

const getPageNumber = (page, index) => {
    if (!page) {
        return index + 1;
    }
    const value = page.pageNumber || page.number || page.page || page.index;
    return value || index + 1;
};

const buildPageThumbs = (pages, currentIndex, maxVisible = 10) => {
    const total = pages.length;
    if (total === 0) {
        return [];
    }
    if (total <= maxVisible) {
        return pages.map((_, index) => ({ type: 'page', index }));
    }
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(0, currentIndex - half);
    let end = Math.min(total - 1, currentIndex + half);
    const currentWindow = end - start + 1;
    if (currentWindow < windowSize) {
        if (start === 0) {
            end = Math.min(total - 1, end + (windowSize - currentWindow));
        } else if (end === total - 1) {
            start = Math.max(0, start - (windowSize - currentWindow));
        }
    }
    const indices = new Set([0, total - 1]);
    for (let i = start; i <= end; i += 1) {
        indices.add(i);
    }
    const sorted = Array.from(indices).sort((a, b) => a - b);
    const items = [];
    for (let i = 0; i < sorted.length; i += 1) {
        const index = sorted[i];
        if (i > 0 && index - sorted[i - 1] > 1) {
            items.push({ type: 'ellipsis', key: `ellipsis-${sorted[i - 1]}-${index}` });
        }
        items.push({ type: 'page', index });
    }
    return items;
};

const getCommentUserId = (comment) => (
    comment?.userId
    || comment?.user_id
    || comment?.uid
    || comment?.authorId
    || comment?.author_id
    || comment?.user?.id
    || comment?.user?.uid
    || comment?.author?.id
    || null
);
const getCommentNick = (comment) => (
    comment?.nick
    || comment?.nickname
    || comment?.user?.nick
    || comment?.user?.nickname
    || comment?.author?.nick
    || comment?.author?.nickname
    || comment?.author?.name
    || null
);
const buildNickKey = (nick) => `nick:${String(nick).trim().toLowerCase()}`;
const getCommentProfileKey = (comment) => {
    const nick = getCommentNick(comment);
    if (nick) {
        return buildNickKey(nick);
    }
    const userId = getCommentUserId(comment);
    return userId ? `id:${userId}` : null;
};

const MangaReaderPage = () => {
    const { slug } = useParams();
    const location = useLocation();
    const scrolled = useScrolled();
    const [manga, setManga] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [selectedChapterId, setSelectedChapterId] = useState(null);
    const [selectedPageIndex, setSelectedPageIndex] = useState(0);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [commentError, setCommentError] = useState(null);
    const [chapterPages, setChapterPages] = useState([]);
    const [pagesLoading, setPagesLoading] = useState(false);
    const [pagesError, setPagesError] = useState(null);
    const [pageImageUrl, setPageImageUrl] = useState(null);
    const [pageImageKey, setPageImageKey] = useState('');
    const [pageImageLoading, setPageImageLoading] = useState(false);
    const [pageImageReady, setPageImageReady] = useState(false);
    const [commentProfiles, setCommentProfiles] = useState({});
    const [coverUrl, setCoverUrl] = useState(null);
    const [pageTransition, setPageTransition] = useState('');
    // Reader preferences from the settings page, read once per visit.
    const [readerPrefs] = useState(() => ({
        rtl: getMangaPref('readDirection') === 'rtl',
        fit: getMangaPref('pageFit'),
        transition: isMangaPrefOn('pageTransition'),
        preload: isMangaPrefOn('preloadNext'),
        showComments: isMangaPrefOn('showComments'),
    }));
    const [isFullscreen, setIsFullscreen] = useState(() => isMangaPrefOn('openFullscreen'));

    const isAllowed = MangaApiService.isAllowedDomain();
    const hasSession = !!localStorage.getItem('sessionKey');

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const initialChapterId = searchParams.get('chapterId');
    const initialPageIndex = useMemo(() => {
        const raw = Number(searchParams.get('page'));
        if (Number.isFinite(raw) && raw > 0) {
            return raw - 1;
        }
        return 0;
    }, [searchParams]);
    const appliedInitialPage = useRef(false);
    const previousPageIndex = useRef(0);
    const pendingTransition = useRef(null);
    const requestedCommentProfileKeys = useRef(new Set());
    const imageRequestKey = useRef('');
    const preloadedImageKeys = useRef(new Set());

    // Arrow keys page through the chapter. The handlers live in a ref because they are
    // built after the early returns below, while hooks must run on every render.
    const keyNavRef = useRef(null);
    useEffect(() => {
        const onKeyDown = (event) => {
            const nav = keyNavRef.current;
            if (!nav || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
            const target = event.target;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                if (!nav.leftDisabled) nav.left();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                if (!nav.rightDisabled) nav.right();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        if (!isFullscreen) {
            return undefined;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isFullscreen]);

    useEffect(() => {
        if (!isAllowed) {
            return;
        }
        const loadManga = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await MangaApiService.getManga(slug);
                const payload = response?.data || response;
                const hasManga = payload && (payload.id || payload.slug || payload.title || payload.name);
                if (response?.type === 'success' || hasManga) {
                    setManga(payload);
                    document.title = `${payload?.title || payload?.name || 'Манга'} · чтение`;
                } else {
                    setError('Не удалось загрузить мангу.');
                }
            } catch (err) {
                setError(getMangaErrorMessage(err, 'Не удалось загрузить мангу.'));
            } finally {
                setLoading(false);
            }
        };
        loadManga();
    }, [slug, isAllowed]);

    useEffect(() => {
        if (!manga?.id) {
            return;
        }
        const loadChapters = async () => {
            try {
                const response = await MangaApiService.getChapters(manga.id);
                const list = unwrapList(response);
                setChapters(list);
                if (response?.type && response.type !== 'success' && list.length === 0) {
                    setError('Не удалось загрузить главы.');
                }
            } catch (err) {
                setError(getMangaErrorMessage(err, 'Не удалось загрузить главы.'));
            }
        };
        loadChapters();
    }, [manga?.id]);

    useEffect(() => {
        let isActive = true;
        const resolveCover = async () => {
            if (!manga) {
                setCoverUrl(null);
                return;
            }
            const direct = MangaApiService.resolveCoverUrl(manga);
            if (direct) {
                if (isActive) {
                    setCoverUrl(direct);
                }
                return;
            }
            try {
                const resolved = await MangaApiService.resolveCoverUrlAsync(manga);
                if (isActive) {
                    setCoverUrl(resolved);
                }
            } catch (error) {
                if (isActive) {
                    setCoverUrl(null);
                }
            }
        };
        resolveCover();
        return () => {
            isActive = false;
        };
    }, [manga]);

    const sortedChapters = useMemo(() => sortChapters(chapters), [chapters]);

    useEffect(() => {
        if (sortedChapters.length === 0) {
            return;
        }
        if (selectedChapterId) {
            return;
        }
        const chapterFromQuery = sortedChapters.find((chapter) => (
            initialChapterId && String(getChapterId(chapter)) === String(initialChapterId)
        ));
        const fallback = chapterFromQuery || sortedChapters[0];
        const fallbackId = getChapterId(fallback);
        if (fallbackId !== undefined && fallbackId !== null) {
            setSelectedChapterId(String(fallbackId));
        }
    }, [sortedChapters, selectedChapterId, initialChapterId]);

    const selectedChapter = useMemo(() => {
        return sortedChapters.find((chapter) => String(getChapterId(chapter)) === String(selectedChapterId)) || null;
    }, [sortedChapters, selectedChapterId]);
    const selectedChapterIndex = useMemo(
        () => sortedChapters.findIndex((chapter) => String(getChapterId(chapter)) === String(selectedChapterId)),
        [sortedChapters, selectedChapterId]
    );
    const hasNextChapter = selectedChapterIndex >= 0 && selectedChapterIndex < sortedChapters.length - 1;

    useEffect(() => {
        if (manga?.id && selectedChapterId) {
            saveMangaProgress(manga.id, selectedChapterId);
        }
    }, [manga?.id, selectedChapterId]);
    const nextChapter = hasNextChapter ? sortedChapters[selectedChapterIndex + 1] : null;
    const prevChapter = selectedChapterIndex > 0 ? sortedChapters[selectedChapterIndex - 1] : null;

    useEffect(() => {
        let isActive = true;
        setPagesError(null);
        setChapterPages([]);
        setPagesLoading(false);
        if (!selectedChapter) {
            return () => {
                isActive = false;
            };
        }
        const localPages = normalizePages(selectedChapter);
        if (localPages.length > 0) {
            setChapterPages(localPages);
            return () => {
                isActive = false;
            };
        }
        const chapterId = getChapterId(selectedChapter);
        if (!chapterId) {
            return () => {
                isActive = false;
            };
        }
        setPagesLoading(true);
        const loadPages = async () => {
            try {
                const response = await MangaApiService.getChapterPages(chapterId);
                const list = unwrapPagesResponse(response);
                if (isActive) {
                    setChapterPages(list);
                }
            } catch (err) {
                if (isActive) {
                    setChapterPages([]);
                    setPagesError(getMangaErrorMessage(err, 'Не удалось загрузить страницы.'));
                }
            } finally {
                if (isActive) {
                    setPagesLoading(false);
                }
            }
        };
        loadPages();
        return () => {
            isActive = false;
        };
    }, [selectedChapter, selectedChapterId]);

    const pages = useMemo(() => normalizePages(chapterPages), [chapterPages]);
    const currentImageKey = useMemo(
        () => `${selectedChapterId || 'chapter'}-${selectedPageIndex}`,
        [selectedChapterId, selectedPageIndex]
    );
    const pageThumbs = useMemo(
        () => buildPageThumbs(pages, selectedPageIndex),
        [pages, selectedPageIndex]
    );
    const currentImageUrl = pageImageKey === currentImageKey ? pageImageUrl : null;
    const isPageImagePending = pageImageLoading || pageImageKey !== currentImageKey;

    useLayoutEffect(() => {
        if (!pendingTransition.current && previousPageIndex.current === selectedPageIndex) {
            return;
        }
        const direction = pendingTransition.current
            || (selectedPageIndex > previousPageIndex.current ? 'next' : 'prev');
        setPageTransition(readerPrefs.transition ? direction : '');
        previousPageIndex.current = selectedPageIndex;
        pendingTransition.current = null;
    }, [selectedPageIndex, selectedChapterId, readerPrefs.transition]);

    useEffect(() => {
        if (!pageTransition) {
            return;
        }
        const shouldClear = pageImageReady || (!pageImageLoading && !currentImageUrl);
        if (!shouldClear) {
            return;
        }
        const handle = setTimeout(() => setPageTransition(''), 260);
        return () => clearTimeout(handle);
    }, [pageTransition, pageImageReady, pageImageLoading, currentImageUrl]);

    useEffect(() => {
        if (pages.length === 0) {
            setSelectedPageIndex(0);
            return;
        }
        if (selectedPageIndex >= pages.length) {
            setSelectedPageIndex(pages.length - 1);
        }
    }, [pages, selectedPageIndex]);

    useEffect(() => {
        if (!selectedChapterId) {
            appliedInitialPage.current = false;
            return;
        }
        if (!initialChapterId || String(initialChapterId) !== String(selectedChapterId)) {
            return;
        }
        if (appliedInitialPage.current || initialPageIndex <= 0) {
            return;
        }
        if (pages.length > initialPageIndex) {
            setSelectedPageIndex(initialPageIndex);
            appliedInitialPage.current = true;
        }
    }, [selectedChapterId, initialChapterId, initialPageIndex, pages.length]);

    const currentPage = pages[selectedPageIndex];
    const currentPageNumber = pages.length > 0
        ? getPageNumber(currentPage, selectedPageIndex)
        : 0;
    const pageBucket = selectedChapter?.pagesStorageBucket || selectedChapter?.storageBucket || selectedChapter?.bucket;

    useEffect(() => {
        let isActive = true;
        const requestKey = currentImageKey;
        imageRequestKey.current = requestKey;
        setPageImageReady(false);
        setPageImageLoading(Boolean(currentPage));
        if (!currentPage) {
            if (isActive) {
                setPageImageUrl(null);
                setPageImageKey(requestKey);
                setPageImageLoading(false);
            }
            return () => {
                isActive = false;
            };
        }
        const resolvePageImage = async () => {
            const direct = MangaApiService.resolveFileUrl(currentPage, pageBucket);
            if (direct) {
                if (isActive && imageRequestKey.current === requestKey) {
                    setPageImageUrl(direct);
                    setPageImageKey(requestKey);
                }
                return;
            }
            try {
                const resolved = await MangaApiService.resolveFileUrlAsync(currentPage, pageBucket);
                if (isActive && imageRequestKey.current === requestKey) {
                    setPageImageUrl(resolved || null);
                    setPageImageKey(requestKey);
                    if (!resolved) {
                        setPageImageLoading(false);
                    }
                }
            } catch (error) {
                if (isActive && imageRequestKey.current === requestKey) {
                    setPageImageUrl(null);
                    setPageImageKey(requestKey);
                    setPageImageLoading(false);
                }
            }
        };
        resolvePageImage();
        return () => {
            isActive = false;
        };
    }, [currentPage, pageBucket, currentImageKey]);

    useEffect(() => {
        if (!readerPrefs.preload) {
            return;
        }
        const nextIndex = selectedPageIndex + 1;
        if (nextIndex < 0 || nextIndex >= pages.length) {
            return;
        }
        const nextPage = pages[nextIndex];
        if (!nextPage) {
            return;
        }
        const nextKey = `${selectedChapterId || 'chapter'}-${nextIndex}`;
        if (preloadedImageKeys.current.has(nextKey)) {
            return;
        }
        let isActive = true;
        const preloadNext = async () => {
            try {
                const direct = MangaApiService.resolveFileUrl(nextPage, pageBucket);
                const url = direct || await MangaApiService.resolveFileUrlAsync(nextPage, pageBucket);
                if (!isActive || !url) {
                    return;
                }
                const img = new Image();
                img.src = url;
                preloadedImageKeys.current.add(nextKey);
            } catch (error) {
                if (!isActive) {
                    return;
                }
            }
        };
        preloadNext();
        return () => {
            isActive = false;
        };
    }, [pages, selectedPageIndex, selectedChapterId, pageBucket]);

    const loadComments = async () => {
        if (!selectedChapter || pages.length === 0) {
            setComments([]);
            return;
        }
        setCommentError(null);
        try {
            const response = await MangaApiService.getPageComments(getChapterId(selectedChapter), currentPageNumber);
            const list = Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response)
                    ? response
                    : [];
            if (response?.type === 'success' || Array.isArray(response)) {
                setComments(list);
            } else {
                setCommentError('Не удалось загрузить комментарии.');
            }
        } catch (err) {
            setCommentError(getMangaErrorMessage(err, 'Не удалось загрузить комментарии.'));
        }
    };

    useEffect(() => {
        loadComments();
    }, [selectedChapterId, selectedPageIndex, pages.length]);

    useEffect(() => {
        let isActive = true;
        const targets = new Map();
        comments.forEach((comment) => {
            const nick = getCommentNick(comment);
            const userId = getCommentUserId(comment);
            const key = nick ? buildNickKey(nick) : (userId ? `id:${userId}` : null);
            if (!key || commentProfiles[key] || requestedCommentProfileKeys.current.has(key)) {
                return;
            }
            targets.set(key, { key, nick, userId });
            requestedCommentProfileKeys.current.add(key);
        });
        const pending = Array.from(targets.values());
        if (pending.length === 0) {
            return () => {
                isActive = false;
            };
        }
        const loadProfiles = async () => {
            for (const target of pending) {
                try {
                    let profile = null;
                    if (target.nick) {
                        const response = await ApiService.get(target.nick);
                        const payload = response?.data || response;
                        profile = payload?.data || payload;
                    }
                    if (!isActive || !profile) {
                        continue;
                    }
                    const resolvedNick = profile?.nickname || profile?.nick || target.nick;
                    const resolvedId = profile?.id ?? target.userId;
                    setCommentProfiles((prev) => {
                        const next = { ...prev, [target.key]: profile };
                        if (resolvedNick) {
                        const nickKey = buildNickKey(resolvedNick);
                        next[nickKey] = profile;
                        requestedCommentProfileKeys.current.add(nickKey);
                    }
                    if (resolvedId !== undefined && resolvedId !== null) {
                        const idKey = `id:${resolvedId}`;
                        next[idKey] = profile;
                        requestedCommentProfileKeys.current.add(idKey);
                    }
                    return next;
                });
                } catch (error) {
                    if (!isActive) {
                        return;
                    }
                }
            }
        };
        loadProfiles();
        return () => {
            isActive = false;
        };
    }, [comments, commentProfiles]);

    const resolvedComments = useMemo(() => (
        comments.map((comment) => {
            if (comment?.user || comment?.author) {
                return comment;
            }
            const key = getCommentProfileKey(comment);
            if (!key) {
                return comment;
            }
            const profile = commentProfiles[key];
            if (!profile) {
                return comment;
            }
            return { ...comment, user: profile };
        })
    ), [comments, commentProfiles]);

    const handleSendComment = async () => {
        if (!commentText.trim() || !selectedChapter) {
            return;
        }
        setCommentError(null);
        try {
            await MangaApiService.leavePageComment(getChapterId(selectedChapter), currentPageNumber, commentText.trim());
            setCommentText('');
            loadComments();
        } catch (err) {
            if (err?.response?.status === 400) {
                setCommentError('Не удалось отправить комментарий: требуется числовой id пользователя.');
                return;
            }
            setCommentError(getMangaErrorMessage(err, 'Не удалось отправить комментарий.'));
        }
    };

    if (!isAllowed) {
        return (
            <PageShell>
                <MangaAccessNotice />
            </PageShell>
        );
    }

    if (loading) {
        return (
            <PageShell>
                <div className="loading-con">
                    <div className="loader" />
                    <p className="ui-status">Загружаем мангу…</p>
                </div>
            </PageShell>
        );
    }

    if (!manga) {
        return (
            <PageShell>
                <div className="ui-empty">
                    <h1 className="ui-empty__title">Манга не найдена</h1>
                    <p className="ui-empty__text">{error || 'Возможно, её удалили или ссылка устарела.'}</p>
                    <Link className="ui-btn ui-btn--secondary" to="/manga">В каталог манги</Link>
                </div>
            </PageShell>
        );
    }

    const title = manga.title || manga.name || manga.slug || 'Манга';
    const handlePrevPage = () => {
        setSelectedPageIndex((prev) => Math.max(0, prev - 1));
    };
    const handleNextPage = () => {
        if (pages.length === 0) {
            return;
        }
        if (pages.length > 0 && selectedPageIndex >= pages.length - 1) {
            const nextChapterId = nextChapter ? getChapterId(nextChapter) : null;
            if (nextChapterId !== null && nextChapterId !== undefined) {
                pendingTransition.current = 'next';
                setSelectedChapterId(String(nextChapterId));
                setSelectedPageIndex(0);
                appliedInitialPage.current = false;
                return;
            }
        }
        setSelectedPageIndex((prev) => Math.min(pages.length - 1, prev + 1));
    };

    const canGoNext = pages.length > 0 && (selectedPageIndex < pages.length - 1 || hasNextChapter);
    const openChapter = (chapter, transition) => {
        const chapterId = chapter ? getChapterId(chapter) : null;
        if (chapterId === null || chapterId === undefined) {
            return;
        }
        pendingTransition.current = transition;
        setSelectedChapterId(String(chapterId));
        setSelectedPageIndex(0);
        appliedInitialPage.current = false;
    };
    const selectedChapterTitle = selectedChapter ? getChapterTitle(selectedChapter) : null;
    const pageClassName = `manga-reader__page fit-${readerPrefs.fit}${readerPrefs.rtl ? ' is-rtl' : ''}${pageTransition ? ` is-${pageTransition}` : ''}${isFullscreen ? ' manga-reader__page--fullscreen' : ''}`;
    // Right-to-left reading: the left tap zone and the left arrow key go forward.
    const handleLeftZone = readerPrefs.rtl ? handleNextPage : handlePrevPage;
    const handleRightZone = readerPrefs.rtl ? handlePrevPage : handleNextPage;
    const leftDisabled = readerPrefs.rtl ? !canGoNext : selectedPageIndex === 0;
    const rightDisabled = readerPrefs.rtl ? selectedPageIndex === 0 : !canGoNext;
    keyNavRef.current = { left: handleLeftZone, right: handleRightZone, leftDisabled, rightDisabled };

    const pageNode = (
        <div className={pageClassName}>
            {pagesLoading || isPageImagePending ? (
                <div className="manga-reader__loading">
                    <div className="loader" />
                </div>
            ) : null}
            {currentImageUrl ? (
                <img
                    key={currentImageKey}
                    className={`manga-reader__image${pageImageReady ? ' is-ready' : ''}`}
                    src={currentImageUrl}
                    alt={`Страница ${currentPageNumber}`}
                    onLoad={() => {
                        if (imageRequestKey.current !== currentImageKey) {
                            return;
                        }
                        setPageImageReady(true);
                        setPageImageLoading(false);
                    }}
                    onError={() => {
                        if (imageRequestKey.current !== currentImageKey) {
                            return;
                        }
                        setPageImageReady(false);
                        setPageImageLoading(false);
                        setPageImageUrl(null);
                        setPageImageKey(currentImageKey);
                    }}
                />
            ) : (!pagesLoading && !isPageImagePending) ? (
                <div className="manga-empty">Страница недоступна.</div>
            ) : null}
            {pages.length > 0 ? (
                <>
                    <button
                        type="button"
                        className="manga-reader__nav manga-reader__nav--left"
                        onClick={handleLeftZone}
                        disabled={leftDisabled}
                        aria-label={readerPrefs.rtl ? 'Следующая страница' : 'Предыдущая страница'}
                    />
                    <button
                        type="button"
                        className="manga-reader__nav manga-reader__nav--right"
                        onClick={handleRightZone}
                        disabled={rightDisabled}
                        aria-label={readerPrefs.rtl ? 'Предыдущая страница' : 'Следующая страница'}
                    />
                </>
            ) : null}
        </div>
    );

    return (
        <div className={`app manga-reader-page${isFullscreen ? ' is-fullscreen' : ''}`}>
            {!isFullscreen ? <Header scrolled={scrolled} /> : null}
            {isFullscreen ? (
                <div className="manga-reader-fullscreen">
                    <div className="manga-reader-fullscreen-bar">
                        <div className="manga-reader-fullscreen-count">
                            Страница {currentPageNumber} из {pages.length || 0}
                        </div>
                        <button
                            type="button"
                            className="manga-reader-fullscreen-close"
                            onClick={() => setIsFullscreen(false)}
                            aria-label="Закрыть полноэкранный режим"
                        >
                            <CloseIcon size={22} />
                        </button>
                    </div>
                    {pageNode}
                </div>
            ) : (
                <div className="content article manga-reader-content">
                    <div className="container-wrapper">
                        <div className="manga-reader-stage">
                            <div className="manga-reader">
                                <div className="manga-reader-head">
                                    <div className="manga-reader-head__title">
                                        <strong>{selectedChapter ? formatChapterLabel(selectedChapter) : 'Глава'}</strong>
                                        {selectedChapterTitle ? <span>{selectedChapterTitle}</span> : null}
                                    </div>
                                    <div className="manga-reader-controls">
                                        <button
                                            type="button"
                                            className="tab-button"
                                            onClick={() => openChapter(prevChapter, 'prev')}
                                            disabled={!prevChapter}
                                            aria-label="Предыдущая глава"
                                            title="Предыдущая глава"
                                        >
                                            <ChevronsLeftIcon />
                                            <span className="manga-reader-controls__text">Глава</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="tab-button"
                                            onClick={handlePrevPage}
                                            disabled={selectedPageIndex === 0}
                                            aria-label="Предыдущая страница"
                                        >
                                            <ChevronLeftIcon />
                                            <span className="manga-reader-controls__text">Назад</span>
                                        </button>
                                        <span className="manga-reader-counter" aria-live="polite">
                                            {currentPageNumber} / {pages.length || 0}
                                        </span>
                                        <button
                                            type="button"
                                            className="tab-button"
                                            onClick={handleNextPage}
                                            disabled={!canGoNext}
                                            aria-label="Следующая страница"
                                        >
                                            <span className="manga-reader-controls__text">Вперёд</span>
                                            <ChevronRightIcon />
                                        </button>
                                        <button
                                            type="button"
                                            className="tab-button"
                                            onClick={() => openChapter(nextChapter, 'next')}
                                            disabled={!nextChapter}
                                            aria-label="Следующая глава"
                                            title="Следующая глава"
                                        >
                                            <span className="manga-reader-controls__text">Глава</span>
                                            <ChevronsRightIcon />
                                        </button>
                                        <button
                                            type="button"
                                            className="tab-button manga-reader-controls__fullscreen"
                                            onClick={() => setIsFullscreen(true)}
                                            disabled={pages.length === 0}
                                            aria-label="На весь экран"
                                            title="На весь экран"
                                        >
                                            <ExpandIcon />
                                        </button>
                                    </div>
                                </div>
                                {pageNode}
                                {pages.length > 0 ? (
                                    <div className="manga-page-thumbs">
                                        {pageThumbs.map((item, renderIndex) => {
                                            if (item.type === 'ellipsis') {
                                                return (
                                                    <span key={item.key || `ellipsis-${renderIndex}`} className="manga-page-ellipsis">
                                                        ...
                                                    </span>
                                                );
                                            }
                                            const index = item.index;
                                            const number = getPageNumber(pages[index], index);
                                            return (
                                                <button
                                                    key={`${number}-${index}`}
                                                    className={`manga-page-thumb ${index === selectedPageIndex ? 'active' : ''}`}
                                                    onClick={() => setSelectedPageIndex(index)}
                                                >
                                                    {number}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : null}
                                {pagesError ? <div className="manga-inline-warning">{pagesError}</div> : null}
                            </div>
                        </div>

                        <div className="manga-reader-layout">
                            <div className="manga-reader-side">
                                <Link className="manga-reader-thumb" to={`/manga/${slug}`}>
                                    <div className="manga-reader-thumb-cover">
                                        <img src={coverUrl || placeholderCover} alt={title} loading="lazy" />
                                    </div>
                                    <div className="manga-reader-thumb-text">
                                        <div className="manga-reader-thumb-title">{title}</div>
                                        <div className="manga-reader-thumb-sub">К описанию</div>
                                    </div>
                                </Link>

                                <div className="manga-reader-chapters">
                                    <div className="manga-reader-chapters-title">Главы</div>
                                    <MangaChapterNavigator
                                        chapters={sortedChapters}
                                        slug={slug}
                                        variant="sidebar"
                                        currentChapterId={selectedChapterId}
                                        onSelect={(chapter) => {
                                            setSelectedChapterId(String(getChapterId(chapter)));
                                            setSelectedPageIndex(0);
                                            appliedInitialPage.current = false;
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                    />
                                </div>
                            </div>

                            {readerPrefs.showComments ? <div className="manga-reader-comments">
                                <h1>Комментарии страницы</h1>
                                {commentError ? <div className="manga-inline-warning">{commentError}</div> : null}
                                <div className="manga-comment-form">
                                    <textarea
                                        value={commentText}
                                        onChange={(event) => setCommentText(event.target.value)}
                                        placeholder="Напишите комментарий..."
                                        disabled={!hasSession}
                                    />
                                    <div className="manga-comment-actions">
                                        <button type="button" className="ui-btn" onClick={handleSendComment} disabled={!hasSession}>
                                            Отправить
                                        </button>
                                    </div>
                                    {!hasSession ? (
                                        <div className="manga-inline-note">Войдите, чтобы оставлять комментарии.</div>
                                    ) : null}
                                </div>
                                <div className="reviews">
                                    {resolvedComments.length > 0 ? (
                                        resolvedComments.map((comment, index) => (
                                            <MangaCommentComponent key={comment.id || index} comment={comment} />
                                        ))
                                    ) : (
                                        <div className="manga-empty">Комментариев пока нет.</div>
                                    )}
                                </div>
                            </div> : null}
                        </div>

                        {error ? <div className="manga-inline-warning">{error}</div> : null}
                    </div>
                </div>
            )}
            {!isFullscreen ? <Footer /> : null}
        </div>
    );
};

export default MangaReaderPage;
